import dateSvg from '@disy/cadenza-icons/datatype-date.svg';
import durationSvg from '@disy/cadenza-icons/datatype-duration.svg';
import geometrySvg from '@disy/cadenza-icons/datatype-geometry.svg';
import ipSvg from '@disy/cadenza-icons/datatype-ip.svg';
import jsonSvg from '@disy/cadenza-icons/datatype-json.svg';
import numberSvg from '@disy/cadenza-icons/datatype-number.svg';
import rasterSvg from '@disy/cadenza-icons/datatype-raster.svg';
import stringSvg from '@disy/cadenza-icons/datatype-string.svg';
import plainTextSvg from '@disy/cadenza-icons/datatype-txt.svg';
import unknownSvg from '@disy/cadenza-icons/datatype-unknown.svg';
import urlSvg from '@disy/cadenza-icons/datatype-url.svg';
import textXmlSvg from '@disy/cadenza-icons/datatype-xml.svg';
import hierarchySvg from '@disy/cadenza-icons/hierarchy.svg';
import type { IconOptions } from '@disy/ui/icon';
import { icon } from '@disy/ui/icon';
import { LabelWithIcon } from '@disy/ui/label-with-icon';

import { AttributeType, isAttributeType } from 'cadenza/repository/model/attribute-type.gen';
import type { HierarchyType } from 'cadenza/repository/model/hierarchy-type.gen';
import type { LobMediaType } from 'cadenza/repository/model/lob-media-type.gen';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';

import './data-type-icon.css';
import i18n from './data-type-icon.properties';

/** Member data type, used to determine datatype icons and text, combines normal attribute excluding 'LOB', with LOB data types and hierarchy */
export type DataType = Exclude<AttributeType, 'LOB'> | LobMediaType | HierarchyType | 'RASTER';

const ICON_BY_DATA_TYPE: Record<DataType, string> = {
  BIGDECIMAL: numberSvg,
  DATE: dateSvg,
  DOUBLE: numberSvg,
  DURATION: durationSvg,
  GEOMETRY: geometrySvg,
  RASTER: rasterSvg,
  INT: numberSvg,
  LONG: numberSvg,
  STRING: stringSvg,
  IPADDRESS: ipSvg,
  URL: urlSvg,
  UNKNOWN: unknownSvg,
  DATE_HIERARCHY: hierarchySvg,
  MULTI_ATTRIBUTE_HIERARCHY: hierarchySvg,
  PLAIN_TEXT: plainTextSvg,
  TEXT_XML: textXmlSvg,
  JSON: jsonSvg,
};

type AttributeTypeDataInternal = Pick<WorkbookAttributeDto, 'type' | 'lobMediaType' | 'relatedGeometryType'> &
  Partial<Pick<WorkbookAttributeDto, 'hasGeometryRelation'>>;

type HierarchyTypeData = Pick<WorkbookAttributeDto, 'hierarchyType'> & { hierarchyType: HierarchyType };

/**
 * Subset of a workbook AttributeDto that is needed for this module.
 */
export type AttributeTypeData = AttributeTypeDataInternal | HierarchyTypeData;

export function dataTypeIconElements(attribute: AttributeTypeData, options?: IconOptions) {
  if (isAttribute(attribute) && isRaster(attribute) && !attribute.hasGeometryRelation) {
    return [dataTypeIcon('RASTER', options)];
  }
  const typeIcon = dataTypeIcon(getDataType(attribute), options);
  if (isAttribute(attribute) && attribute.hasGeometryRelation && attribute.type !== 'GEOMETRY') {
    return isRaster(attribute) ?
        [typeIcon, '/', dataTypeIcon('RASTER', options)]
      : [typeIcon, '/', dataTypeIcon('GEOMETRY', options)];
  }
  return [typeIcon];
}

export function dataTypeLabel(attribute: AttributeTypeData, { withFunction = false } = {}) {
  const dataType = getDataType(attribute);
  const label = new DataTypeLabel(dataType);
  label.textContent = dataTypeName(dataType, { withFunction });
  return label;
}

const COMPONENT_NAME = 'd-datatype-label';

class DataTypeLabel extends LabelWithIcon {
  constructor(type: DataType) {
    super(() => dataTypeIcon(type, { label: undefined })); // The icon must not repeat the label text
  }
}

customElements.define(COMPONENT_NAME, DataTypeLabel);

export function dataTypeName(dataType: DataType, { withFunction = false } = {}) {
  const result = isAttributeType(dataType) ? AttributeType.i18n(dataType) : i18n(`dataType.<${dataType}>`);
  return result + (withFunction ? ` (${i18n('attribute.function')})` : '');
}

function isRaster(attribute: AttributeTypeData) {
  return isAttribute(attribute) && attribute.relatedGeometryType === 'RASTER';
}

function isAttribute(attribute: AttributeTypeData): attribute is AttributeTypeDataInternal {
  return !('hierarchyType' in attribute && attribute.hierarchyType != null);
}

export function dataTypeIcon(dataType: DataType, options: IconOptions = {}) {
  return icon(ICON_BY_DATA_TYPE[dataType], {
    ...options,
    label: dataTypeName(dataType),
    variant: 'normal',
    size: 'xs',
    styleClass: 'd-data-type-icon',
  });
}

export function getDataType(attribute: AttributeTypeData): DataType {
  if (isAttribute(attribute)) {
    if (attribute.type === 'LOB') {
      return attribute.lobMediaType || 'PLAIN_TEXT';
    }
    return attribute.type;
  }
  return attribute.hierarchyType;
}
